import React from 'react';
import { useResponsive } from '@turn-connect/hooks';
import { usePageMaxWidth } from '../../hooks/useLayoutWidth';

/**
 * Standard content-width tiers for authenticated pages — one source of truth so
 * pages stop drifting to arbitrary caps:
 *  - standard (1200): the design-system "1200px content max" — the default.
 *  - wide (1600): data-dense pages (tables, chart + table) that need more room.
 *  - reading (820): long-form prose (help articles) — a comfortable measure.
 */
export const PAGE_WIDTHS = { standard: 1200, wide: 1600, reading: 820 } as const;
export type PageWidth = keyof typeof PAGE_WIDTHS;

/**
 * Horizontal page gutter. Single-sourced so the header content rail and the
 * page body share identical left/right edges at every breakpoint. Consumed by
 * both PageContainer and HeaderContainer via usePageGutter().
 */
export const PAGE_GUTTER = { mobile: 16, desktop: 20 } as const;

/** Responsive horizontal gutter (px): 16 on mobile, 20 on tablet/desktop. */
export function usePageGutter(): number {
  const { isMobile } = useResponsive();
  return isMobile ? PAGE_GUTTER.mobile : PAGE_GUTTER.desktop;
}

interface PageContainerProps {
  /** Content-width tier (default 'standard' = 1200). */
  width?: PageWidth;
  /** Vertical page inset (px). Horizontal inset always comes from the shared gutter. */
  verticalPadding?: string | number;
  /** Full padding override (escape hatch); when set, takes precedence over the gutter. */
  padding?: string;
  className?: string;
  /** Escape hatch, merged last. */
  style?: React.CSSProperties;
  children: React.ReactNode;
}

/**
 * Centers authenticated page content and applies the chosen width tier, honoring
 * the app-wide full/comfortable layout toggle (full mode fills the screen; the
 * comfortable cap centers via margin auto). Owning the centering + caps + gutter
 * here keeps every page consistent by construction — content is never
 * left-justified against a wide viewport, and it always aligns with the header.
 */
export const PageContainer: React.FC<PageContainerProps> = ({
  width = 'standard',
  verticalPadding = '24px',
  padding,
  className,
  style,
  children,
}) => {
  const maxWidth = usePageMaxWidth(PAGE_WIDTHS[width]);
  const gutter = usePageGutter();
  const resolvedPadding = padding ?? `${typeof verticalPadding === 'number' ? `${verticalPadding}px` : verticalPadding} ${gutter}px`;
  return (
    <div className={className} style={{ maxWidth, width: '100%', margin: '0 auto', padding: resolvedPadding, ...style }}>
      {children}
    </div>
  );
};

export default PageContainer;
